import { Autocomplete, Box, Button, Menu, MenuItem, TextField } from '@mui/material';
import { effectiveFactorValue, getCategory, mappedContractId, personRef } from '@timelog/pm-shared';
import type {
  Allocation,
  EmployeeLookupItem,
  PersonRef,
  PlanNode,
  RoleRef,
} from '@timelog/pm-shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  allocationTotals,
  commitDraft,
  nextAllocationFocus,
  seedLine,
  taskBudgetHours,
} from './allocationOps';
import type { AllocationField, AllocationFocus, DraftLine } from './allocationOps';
import { usePlanEditor } from './editor';
import { personSubtitle, roleCatalogNotice, roleOfPerson } from './personRole';
import { useRoleOptions } from './useRoleOptions';
import type { RoleOption, RolePricing } from './useRoleOptions';

import { useBudgetModel } from '@/analytics';
import { useEmployeeLookup } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MICRO_LABEL_TRACKING, NoticeChip } from '@/components/ui';
import { formatAmount, formatHours, useDebouncedValue } from '@/lib';
import { roleColor } from '@/lib/roleColor';

/**
 * The design's inline resource editor: role, person, hours, computed amount.
 * The lines are measured against the task's budget and never rewrite it
 * (BR-28).
 *
 * Which roles are offered and what an hour of them is worth follows the
 * task's contract (D15, `useRoleOptions`): the default hourly-rate list and no
 * amount on an unmapped task, the contract's card on T&M, the average rate on
 * fixed price. A line is never saved without a role (D16): a line that has
 * none yet is a DRAFT held here, not in the document — picking a role commits
 * it, and picking a person first commits it with the person's default role
 * when that role is on offer, otherwise the role stays for the user to choose.
 * Picking a person on a line that already has a role never changes the role.
 *
 * Keyboard (v8, Area 6): every field is addressable — Enter/Tab commits and
 * advances role → person → hours → next row → Add resource; adding a row
 * lands focus on its role. Fields carry data-alloc-row/-field for the focus
 * walk; MUI's Menu/Autocomplete keep their native arrow-key behaviour.
 */

/**
 * The shared look of an allocation row's fields.
 *
 * The role selector's bordered box is the reference; the person selector and
 * the hours input match it, so the three sit at one height instead of the
 * 25 / 40 / 29 px they used to. `--input-height-small` is the design system's
 * 32px field.
 */
const ROW_FIELD_SX = {
  height: 'var(--input-height-small)',
  border: 'var(--border-width-thin) solid var(--border-divider)',
  borderRadius: 'var(--border-radius-small)',
  bgcolor: 'var(--bg-panel)',
  fontFamily: 'var(--font-family)',
  fontSize: 11,
  color: 'var(--fg-1)',
} as const;

const HeaderLabel = ({ width, label }: { width?: number | string; label: string }) => (
  <Box
    component="span"
    sx={{
      width,
      flex: width ? 'none' : 1,
      fontSize: 10,
      fontWeight: 500,
      letterSpacing: MICRO_LABEL_TRACKING,
      textTransform: 'uppercase',
      color: 'var(--fg-3)',
      textAlign: typeof width === 'number' && width < 100 ? 'right' : 'left',
    }}
  >
    {label}
  </Box>
);

const RolePickerButton = ({
  roleRef,
  options,
  pricing,
  notice,
  rowIndex,
  onPick,
  onAdvance,
}: {
  /** Undefined on a draft line — the role is what the line is waiting for. */
  roleRef: RoleRef | undefined;
  options: RoleOption[];
  pricing: RolePricing;
  /** Why there is nothing to choose, when there is nothing to choose. */
  notice: string | null;
  rowIndex: number;
  onPick: (option: RoleOption) => void;
  /** Called after a pick commits — moves focus to the row's person field. */
  onAdvance: () => void;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { t } = useTranslation('pmPlanningPanel');

  const pickAndAdvance = (apply: () => void) => {
    apply();
    setAnchor(null);
    // The Menu restores focus to its anchor on close; advance after that.
    requestAnimationFrame(() => requestAnimationFrame(onAdvance));
  };

  return (
    <Box sx={{ position: 'relative', width: 154, flex: 'none' }}>
      <Box
        component="button"
        type="button"
        data-automation-id="allocation-role-picker"
        data-alloc-row={rowIndex}
        data-alloc-field="role"
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setAnchor(event.currentTarget as HTMLElement);
          }
        }}
        onClick={(event) => setAnchor(event.currentTarget as HTMLElement)}
        sx={{
          ...ROW_FIELD_SX,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          px: 1,
          cursor: 'pointer',
          ...(roleRef === undefined ? { borderStyle: 'dashed', color: 'var(--fg-3)' } : {}),
          '&:hover': { borderColor: 'var(--border-subtle)' },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: roleRef ? roleColor(roleRef.tlpId) : 'var(--color-fog-60)',
            flex: 'none',
          }}
        />
        <Box
          component="span"
          sx={{
            flex: 1,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {roleRef?.snapshot.name ?? t('allocationEditor.PickARole')}
        </Box>
        <MaterialSymbol name="expand_more" size={14} color="var(--fg-3)" />
      </Box>
      <Menu open={anchor !== null} anchorEl={anchor} onClose={() => setAnchor(null)}>
        {notice ? (
          <Box
            data-automation-id="role-catalog-notice"
            sx={{ px: 2, py: 1, maxWidth: 260, fontSize: 11, color: 'var(--fg-3)' }}
          >
            {notice}
          </Box>
        ) : null}
        {options.map((option) => (
          <MenuItem
            key={`${option.source}-${option.roleRef.tlpId}`}
            selected={option.roleRef.tlpId === roleRef?.tlpId}
            data-automation-id="role-option"
            onClick={() => pickAndAdvance(() => onPick(option))}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: roleColor(option.roleRef.tlpId),
                mr: 1,
                flex: 'none',
              }}
            />
            <Box component="span" sx={{ flex: 1 }}>
              {option.roleRef.snapshot.name}
            </Box>
            {pricing === 'cost-to-cost' ? (
              // Revenue derives from cost here (CALC-24), so the traceable
              // number beside a role is what an hour of it costs.
              <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)', ml: 1 }}>
                {option.costRate !== null
                  ? t('shared.CostPerHour', { amount: formatAmount(option.costRate) })
                  : t('allocationEditor.NoCostPrice')}
              </Box>
            ) : pricing !== 'unpriced' ? (
              <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)', ml: 1 }}>
                {option.rate !== null
                  ? `${formatAmount(option.rate)} kr`
                  : t('allocationEditor.NotOnRateCard')}
              </Box>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

type PersonOption =
  | { kind: 'any' }
  | { kind: 'person'; tlpId: string; name: string; subtitle?: string; item: EmployeeLookupItem };

const PersonPicker = ({
  roleRef,
  personRef: person,
  lookupsEnabled,
  pricing,
  rowIndex,
  onPick,
  onAdvance,
}: {
  roleRef: RoleRef | undefined;
  personRef: PersonRef | undefined;
  lookupsEnabled: boolean;
  /** Under cost-to-cost each person's cost price shows beside their role (CALC-24). */
  pricing: RolePricing;
  rowIndex: number;
  onPick: (option: PersonOption) => void;
  /** Called after a pick or a plain Enter with the popup closed — moves to hours. */
  onAdvance: () => void;
}) => {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);
  const lookup = useEmployeeLookup(debounced, lookupsEnabled);
  const { t } = useTranslation('pmPlanningPanel');

  /*
    Always "Anyone", never "Any {role}". The role is already named in the field
    immediately to the left, so repeating it here made the option longer and
    read as a different KIND of choice than it is: this field only says whether
    a specific person is wanted, and the role answers that for itself.
  */
  const anyLabel = t('shared.Anyone');
  const options: PersonOption[] = [
    { kind: 'any' },
    ...(lookup.data?.items ?? []).map((item): PersonOption => ({
      kind: 'person',
      tlpId: item.tlpId,
      name: item.name,
      // The person's default role (their standard hourly rate) — never the
      // job title or TimeLog RBAC role, which are other dimensions entirely.
      // Under cost-to-cost their cost price joins it: that is what the line's
      // revenue derives from (CALC-24), so it has to be traceable here.
      subtitle: personSubtitle(item, pricing, t),
      item,
    })),
  ];

  return (
    <Autocomplete<PersonOption, false, true>
      size="small"
      data-automation-id="allocation-person-field"
      disableClearable
      disabled={!lookupsEnabled}
      options={options}
      filterOptions={(candidates) => candidates}
      value={
        person
          ? {
              kind: 'person',
              tlpId: person.tlpId,
              name: person.snapshot.name,
              item: { tlpId: person.tlpId, name: person.snapshot.name },
            }
          : { kind: 'any' }
      }
      isOptionEqualToValue={(option, value) =>
        option.kind === 'any'
          ? value.kind === 'any'
          : value.kind === 'person' && option.tlpId === value.tlpId
      }
      getOptionLabel={(option) => (option.kind === 'any' ? anyLabel : option.name)}
      onChange={(_event, option) => {
        onPick(option);
        requestAnimationFrame(() => requestAnimationFrame(onAdvance));
      }}
      onInputChange={(_event, value, reason) => {
        if (reason === 'input') setQuery(value);
      }}
      renderOption={(props, option) => (
        <li {...props} data-automation-id="person-option">
          {option.kind === 'any' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MaterialSymbol name="person" size={17} color="var(--fg-3)" />
              {anyLabel}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                // One line per person: the popup grows to fit (see slotProps.popper)
                // rather than wrapping "Role · 700 kr/h cost" under the name.
                whiteSpace: 'nowrap',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: roleColor(roleRef?.tlpId ?? option.item.defaultRoleServiceId ?? ''),
                  color: 'var(--color-black-00)',
                  fontSize: 10,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                {option.name.charAt(0)}
              </Box>
              <Box component="span" sx={{ flex: 1 }}>
                {option.name}
              </Box>
              {option.subtitle ? (
                <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {option.subtitle}
                </Box>
              ) : null}
            </Box>
          )}
        </li>
      )}
      sx={{
        flex: 1,
        minWidth: 0,
        // MUI's outlined input is 40px with its own notched fieldset; both are
        // replaced so the field matches the role selector beside it.
        '& .MuiAutocomplete-inputRoot': {
          ...ROW_FIELD_SX,
          py: 0,
          pl: 1,
          '& .MuiAutocomplete-input': { p: 0, fontSize: 11 },
          '& fieldset': { border: 'none' },
          '&.Mui-focused': { borderColor: 'var(--color-blue-80)' },
          '&:hover:not(.Mui-focused)': { borderColor: 'var(--border-subtle)' },
        },
      }}
      // MUI sizes the popup to the input by default; ours is a narrow field in a
      // dense row, so the list takes the width its longest entry needs instead
      // (bounded, so a run-away name cannot span the page), and never wraps.
      slotProps={{ popper: { style: { width: 'max-content', minWidth: 260, maxWidth: 520 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={anyLabel}
          title={lookupsEnabled ? undefined : t('allocationEditor.PeopleLookupDisabledMode')}
          onKeyDown={(event) => {
            // Enter with the popup OPEN belongs to MUI (it selects the
            // highlighted option and onChange advances); with it closed, Enter
            // keeps the current person and moves on. The input's aria-expanded
            // is the reliable open/closed signal at keydown time.
            const popupOpen =
              (event.target as HTMLElement).getAttribute('aria-expanded') === 'true';
            if (event.key === 'Enter' && !popupOpen) {
              event.preventDefault();
              onAdvance();
            }
          }}
          slotProps={{
            ...params.InputProps,
            htmlInput: {
              ...params.inputProps,
              'data-automation-id': 'allocation-person-input',
              'data-alloc-row': rowIndex,
              'data-alloc-field': 'person',
            },
          }}
        />
      )}
    />
  );
};

/** The person snapshot a lookup hit becomes: name + the cost schedule, when exposed (D14). */
const personFromItem = (item: EmployeeLookupItem): PersonRef =>
  personRef(item.tlpId, item.name, new Date().toISOString(), item.costRates);

const HoursInput = ({
  rowIndex,
  value,
  onChange,
  onEnter,
  onTab,
  onShiftTab,
}: {
  rowIndex: number;
  value: number;
  onChange: (hours: number) => void;
  onEnter: () => void;
  onTab: () => void;
  onShiftTab: () => void;
}) => (
  <Box
    component="input"
    inputMode="numeric"
    data-automation-id="allocation-hours-input"
    data-alloc-row={rowIndex}
    data-alloc-field="hours"
    value={String(value)}
    onKeyDown={(event) => {
      // Values commit onChange; the keys only decide where to go next — Enter
      // is "done" (closes), Tab walks on and appends a line when it runs off
      // the end of the table.
      if (event.key === 'Enter') {
        event.preventDefault();
        onEnter();
      } else if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();
        onTab();
      } else if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        onShiftTab();
      }
    }}
    onChange={(event) => {
      const parsed = Number((event.target as HTMLInputElement).value.replace(',', '.'));
      onChange(Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) / 100 : 0);
    }}
    sx={{
      ...ROW_FIELD_SX,
      width: 62,
      flex: 'none',
      // The figure itself stays mono and full size — only the box matches its neighbours.
      fontFamily: 'var(--font-family-mono)',
      fontSize: 14,
      fontWeight: 500,
      textAlign: 'right',
      px: 1,
      outline: 'none',
      '&:focus': { borderColor: 'var(--color-blue-80)' },
    }}
  />
);

const RemoveButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('pmPlanningPanel');
  return (
    <Box
      component="button"
      type="button"
      title={t('allocationEditor.Remove')}
      data-automation-id="allocation-remove"
      onClick={onClick}
      sx={{
        width: 24,
        height: 24,
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        bgcolor: 'transparent',
        borderRadius: 'var(--border-radius-small)',
        color: 'var(--fg-3)',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'var(--color-coral-10)', color: 'var(--color-coral-80)' },
      }}
    >
      <MaterialSymbol name="close" size={16} />
    </Box>
  );
};

export type AllocationEditorProps = {
  node: PlanNode;
  /** Enter in an hours field is "done": the caller closes the overlay. */
  onRequestClose?: () => void;
};

export const AllocationEditor = ({ node, onRequestClose }: AllocationEditorProps) => {
  const { dispatch, resolveRate, state, contractInputs } = usePlanEditor();
  const budgetModel = useBudgetModel();
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon']);
  /**
   * The Own Work factor as the card shows it (CALC-20) — what a derived
   * Cost-To-Cost amount was multiplied by, for the amount cell's tooltip.
   */
  const ownFactorOf = (id: string | undefined): number => {
    const contract = contractInputs.find((candidate) => candidate.id === id);
    return contract
      ? effectiveFactorValue(
          budgetModel.config,
          'own',
          getCategory(contract.document, 'own').targetFactor,
        )
      : 1;
  };

  /*
    Read live, not captured at mount. Allocating no longer rewrites the budget
    (BR-28/ADR 0030), so the document keeps it and everything measured against
    it — the remainder that seeds the next line, the over-allocation signal —
    stays correct even as the budget itself is edited in the row behind.
  */
  const budgetHours = taskBudgetHours(node);

  const contractId = mappedContractId(node);
  const { roleOptions, lookupsEnabled, pricing, catalogFailed, source, legalEntityName } =
    useRoleOptions(contractId);
  const catalogNotice = roleCatalogNotice(
    {
      optionCount: roleOptions.length,
      lookupsEnabled,
      failed: catalogFailed,
      source,
      legalEntityName,
    },
    t,
  );

  /**
   * The one line being composed without a role yet (D16); null when every line
   * is saved. Opening on an unallocated task whose role is unknown starts
   * with the seed as a draft (the role-carrying seed is dispatched below) — the
   * common case, one person and all the hours, is already right either way.
   */
  const [draft, setDraft] = useState<DraftLine | null>(() => {
    if (node.allocations.length > 0) return null;
    const seeded = seedLine(node, budgetHours);
    return seeded.kind === 'draft' ? seeded.draft : null;
  });

  /*
    What the task held when this popover opened. The editor commits live — a
    line lands the moment it has a role, and hours commit on Enter or blur — so
    Cancel has to put this back. Without it the button would be decoration.
    Safe as a mount-time ref: ResourcesPopover renders this editor only while
    open, so it remounts on every open.
  */
  const openingAllocationsRef = useRef<Allocation[]>(node.allocations);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<AllocationFocus | null>(null);

  const focusTarget = (target: AllocationFocus | null) => {
    if (!target || !containerRef.current) return;
    const element =
      target.row === 'add'
        ? containerRef.current.querySelector<HTMLElement>('[data-automation-id="allocation-add"]')
        : containerRef.current.querySelector<HTMLElement>(
            `[data-alloc-row="${target.row}"][data-alloc-field="${target.field}"]`,
          );
    element?.focus();
  };

  // Adding a row: focus its role once the new row has rendered (ref, not
  // state — nothing re-renders for a focus move).
  useEffect(() => {
    if (!pendingFocusRef.current) return;
    focusTarget(pendingFocusRef.current);
    pendingFocusRef.current = null;
  });

  const rowCount = node.allocations.length + (draft ? 1 : 0);
  const draftRow = draft ? node.allocations.length : -1;

  const advanceFrom = (row: number, field: AllocationField, direction: 1 | -1 = 1) =>
    focusTarget(nextAllocationFocus({ row, field }, rowCount, direction));

  const setAllocations = (allocations: Allocation[]) =>
    dispatch({ type: 'SET_ALLOCATIONS', nodeId: node.id, allocations });

  const updateAllocation = (id: string, patch: Partial<Allocation>) =>
    setAllocations(
      node.allocations.map((allocation) =>
        allocation.id === id ? { ...allocation, ...patch } : allocation,
      ),
    );

  /**
   * One new line seeded with what is left of the captured budget: saved at
   * once when it inherits the task's role (the first line), otherwise a draft
   * waiting for its role. A second "Add" while a draft is open just focuses it.
   */
  const addLine = (focusRole = true) => {
    if (draft) {
      if (focusRole) pendingFocusRef.current = { row: draftRow, field: 'role' };
      return;
    }
    const seeded = seedLine(node, budgetHours);
    if (seeded.kind === 'allocation') {
      setAllocations([...node.allocations, seeded.allocation]);
      if (focusRole) pendingFocusRef.current = { row: node.allocations.length, field: 'role' };
    } else {
      setDraft(seeded.draft);
      if (focusRole) pendingFocusRef.current = { row: node.allocations.length, field: 'role' };
    }
  };

  /**
   * Opening on an unallocated task that carries a role seeds one SAVED line
   * with the task's full budget, role and assignee, so the common case — one
   * person, all the hours — is already right and you only confirm. (Without a
   * role the seed is the draft initialised above.) Guarded by a ref rather
   * than the allocation count: deleting the last line must not re-seed it.
   */
  const seededOnOpenRef = useRef(false);
  useEffect(() => {
    if (seededOnOpenRef.current) return;
    seededOnOpenRef.current = true;
    if (node.allocations.length === 0) {
      const seeded = seedLine(node, budgetHours);
      if (seeded.kind === 'allocation') setAllocations([seeded.allocation]);
    }
    // Mount-only: this closes over the mount render's node, which is the
    // empty one we mean to seed from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Set when Enter refused to close on the role-less draft; cleared as soon as it resolves. */
  const [unresolved, setUnresolved] = useState(false);

  const commitDraftWith = (roleRef: RoleRef) => {
    if (!draft) return;
    setAllocations([...node.allocations, commitDraft(draft, roleRef)]);
    setDraft(null);
    setUnresolved(false);
  };

  /**
   * Person picked on the draft (D16, person first): the role the person brings
   * is the suggestion — their default role, what they are already allocated
   * at on this plan, or a name match — and only ever one this task's contract
   * offers (`roleOfPerson`). Taken when found, left for the user to pick when
   * not. Never a role the contract cannot price.
   */
  const pickPersonOnDraft = (option: PersonOption) => {
    if (!draft) return;
    if (option.kind === 'any') {
      setDraft({ hours: draft.hours });
      return;
    }
    const person = personFromItem(option.item);
    const suggested = roleOfPerson(option.item, roleOptions, state.document.plan);
    if (suggested) {
      setAllocations([
        ...node.allocations,
        commitDraft({ hours: draft.hours, personRef: person }, suggested),
      ]);
      setDraft(null);
      setUnresolved(false);
    } else {
      setDraft({ ...draft, personRef: person });
    }
  };

  const rateOf = (allocation: Allocation) =>
    resolveRate(contractId, allocation.roleRef, allocation.personRef).rate;
  const totals = allocationTotals(node.allocations, budgetHours, rateOf, draft?.hours ?? 0);
  const overHours = totals.overHours;
  // Only "no contract" prices nothing (D7); Cost-To-Cost prices from cost (CALC-24).
  const unpriced = pricing === 'unpriced';
  const ownFactor = ownFactorOf(contractId);

  /**
   * Enter in an hours field means done. It closes — unless the draft is still
   * waiting for its role, in which case closing would silently drop hours
   * nobody is committed to, so the inline error appears instead.
   */
  const commitFromHours = (rowIndex: number) => {
    if (draft && rowIndex === draftRow) {
      setUnresolved(true);
      return;
    }
    if (draft && draft.hours > 0) {
      setUnresolved(true);
      pendingFocusRef.current = { row: draftRow, field: 'role' };
      return;
    }
    setUnresolved(false);
    onRequestClose?.();
  };

  /**
   * Save closes. It changes nothing on its own — every edit is already in the
   * document — but it refuses the same half-finished draft `commitFromHours`
   * refuses, rather than dropping hours nobody is committed to (D16).
   */
  const save = () => {
    if (draft && draft.hours > 0) {
      setUnresolved(true);
      pendingFocusRef.current = { row: draftRow, field: 'role' };
      return;
    }
    setUnresolved(false);
    onRequestClose?.();
  };

  /** Cancel puts back the allocations this popover opened on, then closes. */
  const cancel = () => {
    dispatch({
      type: 'SET_NODE_FIELDS',
      nodeId: node.id,
      patch: { allocations: openingAllocationsRef.current },
    });
    setUnresolved(false);
    onRequestClose?.();
  };

  /** Tab off the last hours field appends the next line rather than leaving the table. */
  const tabFromHours = (rowIndex: number) => {
    const next = nextAllocationFocus({ row: rowIndex, field: 'hours' }, rowCount);
    if (next && 'row' in next && next.row === 'add') {
      addLine();
      return;
    }
    focusTarget(next);
  };

  const amountCell = (allocation: Allocation) => {
    const sell = resolveRate(contractId, allocation.roleRef, allocation.personRef);
    if (unpriced) {
      return (
        <Box
          component="span"
          data-automation-id="allocation-budget"
          data-priced="false"
          title={t('allocationEditor.UnpricedAmountTitle')}
          sx={{ width: 74, flex: 'none', textAlign: 'right', fontSize: 11, color: 'var(--fg-3)' }}
        >
          —
        </Box>
      );
    }
    const missing = sell.state === 'missing-role';
    const derived = sell.state === 'derived';
    return (
      <Box
        component="span"
        data-automation-id="allocation-budget"
        data-priced="true"
        data-derived={derived ? 'true' : undefined}
        title={
          missing
            ? t('allocationEditor.MissingRoleTitle')
            : derived
              ? t('allocationEditor.DerivedTitle', {
                  cost: formatAmount(ownFactor === 0 ? 0 : sell.rate / ownFactor),
                  hours: String(allocation.hours),
                  factor: String(ownFactor),
                })
              : undefined
        }
        sx={{
          width: 74,
          flex: 'none',
          textAlign: 'right',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 11,
          color: missing ? 'var(--color-warning-80)' : 'var(--fg-2)',
        }}
      >
        {formatAmount(allocation.hours * sell.rate)}
      </Box>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      data-automation-id="allocation-editor"
      data-pricing={pricing}
    >
      {rowCount > 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, pb: 0.5 }}>
          <HeaderLabel width={154} label={t('pmCommon:term.Role')} />
          <HeaderLabel label={t('allocationEditor.Person')} />
          <HeaderLabel width={62} label={t('pmCommon:term.Hours')} />
          <HeaderLabel width={74} label={t('allocationEditor.Amount')} />
          <Box component="span" sx={{ width: 24, flex: 'none' }} />
        </Box>
      ) : null}

      {node.allocations.map((allocation, rowIndex) => (
        <Box
          key={allocation.id}
          data-automation-id="allocation-row"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <RolePickerButton
            roleRef={allocation.roleRef}
            options={roleOptions}
            pricing={pricing}
            notice={catalogNotice}
            rowIndex={rowIndex}
            // Role first, then person: the role sticks whoever is picked later (D16).
            onPick={(option) => updateAllocation(allocation.id, { roleRef: option.roleRef })}
            onAdvance={() => advanceFrom(rowIndex, 'role')}
          />
          <PersonPicker
            roleRef={allocation.roleRef}
            personRef={allocation.personRef}
            lookupsEnabled={lookupsEnabled}
            pricing={pricing}
            rowIndex={rowIndex}
            // A person picked after the role never overrides it — even one
            // whose default role differs (D16).
            onPick={(option) =>
              updateAllocation(allocation.id, {
                personRef: option.kind === 'any' ? undefined : personFromItem(option.item),
              })
            }
            onAdvance={() => advanceFrom(rowIndex, 'person')}
          />
          <HoursInput
            rowIndex={rowIndex}
            value={allocation.hours}
            onChange={(hours) => updateAllocation(allocation.id, { hours })}
            onEnter={() => commitFromHours(rowIndex)}
            onTab={() => tabFromHours(rowIndex)}
            onShiftTab={() => advanceFrom(rowIndex, 'hours', -1)}
          />
          {amountCell(allocation)}
          <RemoveButton
            onClick={() =>
              setAllocations(node.allocations.filter((entry) => entry.id !== allocation.id))
            }
          />
        </Box>
      ))}

      {draft ? (
        <Box
          key="draft"
          data-automation-id="allocation-row"
          data-draft="true"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <RolePickerButton
            roleRef={undefined}
            options={roleOptions}
            pricing={pricing}
            notice={catalogNotice}
            rowIndex={draftRow}
            onPick={(option) => commitDraftWith(option.roleRef)}
            onAdvance={() => advanceFrom(draftRow, 'role')}
          />
          <PersonPicker
            roleRef={undefined}
            personRef={draft.personRef}
            lookupsEnabled={lookupsEnabled}
            pricing={pricing}
            rowIndex={draftRow}
            onPick={pickPersonOnDraft}
            onAdvance={() => advanceFrom(draftRow, 'person')}
          />
          <HoursInput
            rowIndex={draftRow}
            value={draft.hours}
            onChange={(hours) => setDraft({ ...draft, hours })}
            onEnter={() => commitFromHours(draftRow)}
            onTab={() => tabFromHours(draftRow)}
            onShiftTab={() => advanceFrom(draftRow, 'hours', -1)}
          />
          <Box
            component="span"
            data-automation-id="allocation-budget"
            data-priced="false"
            sx={{ width: 74, flex: 'none', textAlign: 'right', fontSize: 11, color: 'var(--fg-3)' }}
          >
            —
          </Box>
          <RemoveButton
            onClick={() => {
              setDraft(null);
              setUnresolved(false);
            }}
          />
        </Box>
      ) : null}

      {draft?.personRef && !unresolved ? (
        <Box
          data-automation-id="allocation-role-hint"
          sx={{ fontSize: 11, color: 'var(--fg-3)', px: 0.5 }}
        >
          {t('allocationEditor.RoleHint', { name: draft.personRef.snapshot.name })}
        </Box>
      ) : null}

      {unresolved ? (
        <Box
          data-automation-id="allocation-unresolved-error"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 11,
            color: 'var(--color-coral-80)',
            px: 0.5,
          }}
        >
          <MaterialSymbol name="error" size={14} color="var(--color-coral-80)" />
          {t('allocationEditor.UnresolvedError')}
        </Box>
      ) : null}

      {pricing === 'unpriced' ? (
        <Box
          data-automation-id="allocation-unpriced-hint"
          sx={{ fontSize: 11, color: 'var(--fg-3)', px: 0.5 }}
        >
          {t('allocationEditor.UnpricedHint')}
        </Box>
      ) : null}

      {/*
        Keyed to the LIVE footer total, not to the saved node: the warning has
        to appear as you type, including on the draft that has no role yet, and
        on a task with no budget at all. Yellow and live, never a step in the
        way (designer, 2026-10-18): the red two-step Save read as blocking.
        Under budget nothing is drawn — the remainder already shows itself by
        pre-filling the next line.
      */}
      {overHours > 0 ? (
        // Drawn like Financials' "not yet distributed" chip (designer,
        // 2026-10-20): a small yellow info line, one weight, no title. The
        // figures are in the total below, so it carries only the advice.
        <NoticeChip tone="warning" icon="info" automationId="allocation-over-budget-alert">
          {t('allocationEditor.OverBudgetTitle', { over: formatHours(overHours) })}{' '}
          {t('allocationEditor.OverBudgetAdvice')}
        </NoticeChip>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mt: 0.5,
        }}
      >
        {/*
          The design system's outlined button rather than a hand-rolled dashed
          pill: this is the panel's main action and it was reading as a
          placeholder.
        */}
        <Button
          variant="outlined"
          size="small"
          data-automation-id="allocation-add"
          onClick={() => addLine()}
          startIcon={<MaterialSymbol name="person_add" size={16} />}
        >
          {t('allocationEditor.AddResource')}
        </Button>
        {rowCount > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
              {t('allocationEditor.Total')}{' '}
              <Box
                component="span"
                data-automation-id="allocation-total-hours"
                data-over={overHours > 0 || undefined}
                sx={{
                  fontFamily: 'var(--font-family-mono)',
                  fontWeight: 500,
                  color: overHours > 0 ? 'var(--fg-warning-text)' : 'var(--fg-1)',
                }}
              >
                {t('shared.HoursValue', { hours: formatHours(totals.hours) })}
              </Box>{' '}
              <Box component="span" data-automation-id="allocation-total-budget-hours">
                {t('allocationEditor.TotalBudgeted', { hours: formatHours(budgetHours) })}
              </Box>
            </Box>
            {unpriced ? (
              <Box
                component="span"
                data-automation-id="allocation-total-budget"
                data-priced="false"
                sx={{ fontSize: 11, color: 'var(--fg-3)' }}
              >
                {t('allocationEditor.Unpriced')}
              </Box>
            ) : (
              <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                <Box
                  component="span"
                  data-automation-id="allocation-total-budget"
                  data-priced="true"
                  sx={{
                    fontFamily: 'var(--font-family-mono)',
                    fontWeight: 500,
                    color: 'var(--fg-1)',
                  }}
                >
                  {formatAmount(totals.budget)}
                </Box>{' '}
                kr
              </Box>
            )}
          </Box>
        ) : null}
      </Box>

      {/* Modal-style actions, bottom right — the shape every dialog here uses. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          mt: 1.5,
          pt: 1.5,
          borderTop: 'var(--border-width-thin) solid var(--border-divider)',
        }}
      >
        <Button
          color="inherit"
          size="small"
          data-automation-id="allocation-cancel"
          onClick={cancel}
        >
          {t('allocationEditor.Cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          data-automation-id="allocation-save"
          onClick={save}
        >
          {t('allocationEditor.Save')}
        </Button>
      </Box>
    </Box>
  );
};
